import type { ReportTypeId } from '../config/reportTypes';
import type { CompanyArchetype } from './company';
import type { SiteContext } from './siteContext';
import type { TypedPreferencesByType, SiteRagReport } from './siteRag';
import type { DeliveryDecision } from './reportContractsV2';

/** Lifecycle status for reload-safe async report generation jobs. */
export type ReportGenerationJobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

/**
 * Exact Site Evidence Document authority selected by a trusted server caller.
 *
 * This is a semantic pin, not a browser input. Report workers that receive it
 * must consume the exact parsed canonical persisted document and must fail if
 * the site's active SED pointer changes before either report commit. JSON
 * whitespace and object-key formatting are deliberately not identity-bearing.
 */
export interface ExpectedSedAuthorityV1 {
  schemaVersion: 'report-sed-authority-v1';
  id: string;
  version: number;
  /** Full canonical parsed SED JSON hash, including conflicts/amendments/timestamps. */
  snapshotHash: string;
  contentHash: string;
  storagePath: string;
}

/** Inputs required to run a typed report generation job. */
export interface ReportGenerationJobRequest {
  /**
   * Runtime-validated enqueue contract version. Legacy clients may omit this;
   * the server upgrades accepted requests to v2 before persistence.
   */
  schemaVersion?: 2;
  /**
   * Server-owned discriminator for web-first decision-analysis refreshes.
   * Analysis-only jobs run the same deterministic facts, narrative and
   * semantic QA pipeline, then stop after the immutable analysis commit.
   * They never invoke the optional compositor/PDF path.
   *
   * This field is intentionally absent from the public V2 request schema:
   * browser callers cannot silently change a requested report into a
   * non-artifact job. The authenticated decision-analysis endpoint and the
   * server invalidation worker are the only writers.
   */
  analysisOnly?: boolean;
  /**
   * Server-owned discriminator for web-first generated products that are not
   * GO decision-analysis domains (currently Custom Brief). Content-only jobs
   * persist an immutable, current semantic report revision and stop before
   * pagination. The public report-generation request schema intentionally
   * excludes this field; only the authenticated report-content endpoint and
   * dependency reconciler may set it.
   */
  contentOnly?: boolean;
  /** Exact server-built input identity expected when a content-only worker starts. */
  expectedReportContentInputHash?: string;
  /**
   * Server-owned exact SED authority for an audited/pinned generation run.
   * Intentionally absent from the strict public V2 request schema: browser
   * callers cannot choose or spoof evidence authority.
   */
  expectedSedAuthority?: ExpectedSedAuthorityV1;
  siteId: string;
  /**
   * Cross-user report generation (admin / company-shared access). Sites live at
   * `users/{ownerUid}/sites/{siteId}`. When the REQUESTER (job `uid`) is not the
   * site owner, the enqueue route resolves the owner from the doc PATH (never
   * client input) AFTER an authorization gate (platform/DA admin OR company
   * subtree READ access) and stamps the OWNER's uid here. The runner reads the
   * SITE / SED / evidence from this namespace, while the job doc + generated
   * report stay under the requester's `uid`.
   *
   * OMITTED for same-user generation (requester === owner), so existing job docs
   * and behaviour are untouched and `stripUndefined` keeps it out of the write.
   */
  ownerUid?: string;
  reportTypeId: ReportTypeId;
  mode?: 'new' | 'update' | 'replace' | 'add-another';
  parentReportId?: string;
  typedPreferences?: TypedPreferencesByType;
  companyArchetypeOverride?: CompanyArchetype;
  documentSelection?: {
    documentRefs: NonNullable<SiteRagReport['documentRefs']>;
    documentSelectionMode: 'auto' | 'mixed' | 'manual';
  };
  siteContextOverride?: SiteContext;
  siteConfigOverrides?: {
    projectIntentOverride?: import('./siteRag').ProjectIntent;
    typedPreferencesOverride?: TypedPreferencesByType;
    siteContextOverride?: SiteContext;
    queuedEvidenceWait?: boolean;
    /** One-run provider coverage waivers, validated at the API boundary. */
    providerCoverageDispatchWaivers?: { providerId: string }[];
    /** One-run citation-density override, retained for existing workflows. */
    allowCitationDensityOverride?: boolean;
    /** Request one focused citation-density regeneration pass. */
    citationDensityRegenerateFocus?: boolean;
    /** Optional citation-support repair focus supplied by the quality UI. */
    citationSupportRegenerateFocus?: string;
    /**
     * Cost P3 — generation tier for this job. Omitted / 'investment-grade'
     * ⇒ today's full path. 'draft' ⇒ cheaper opt-in iteration tier. Truth
     * gates always run; draft output is barred from export.
     */
    generationTier?: import('../config/generationTier').GenerationTier;
    sectionColumnLayouts?: Record<string, 1 | 2 | 3>;
    /**
     * Enqueue-time identity for the complete on-site Test Fit authority chain.
     * The worker must compare this tuple before execution and persistence.
     */
    expectedTestFitAuthorityRef?: import('./generationImpact').TestFitAuthorityRef;
    /**
     * Public Sentiment only: the operator chose "Refresh sentiment scan" on the
     * rerun, so the enqueue re-reads the external sources instead of reusing a
     * recent SED scan (the default). A rescan that reproduces the saved
     * evidence keeps the current SED version. Part of the request identity
     * (request hash / idempotency receipt), like every request field.
     */
    refreshPublicSentimentScan?: true;
  };
  /**
   * Template→report linkage (Phase 1). The client stamps the ACTIVE template
   * (id + its monotonic `version`) selected at generation time so the runner
   * can persist `templateId` + `templateVersionUsed` onto the report doc. Absent
   * when no template is active — additive, never changes generation logic.
   */
  templateId?: string;
  templateVersion?: number;
  /** Client-supplied id so UI can correlate before Firestore round-trip. */
  clientRunId?: string;
  /** Audited one-run citation override envelope. */
  citationOverride?: { token: string; reason: string };
  /** Opaque simulation/evidence adapter contract version consumed by reports. */
  evidenceAdapterVersion?: string;
}

export type ReportGenerationRequestV2 =
  import('./reportContractsV2').ReportGenerationRequestV2;

export type ReportArtifactState =
  | 'not-created'
  | 'pending'
  | 'staged'
  | 'body-ready'
  | 'ready'
  | 'failed';

/**
 * Firestore document at `users/{uid}/reportGenerationJobs/{jobId}`.
 * Written by the server (enqueue + runner); clients subscribe read-only.
 */
/** Server-only reservation for an admin CLI using an exact private source snapshot. */
export interface PrivateLocalReportExecutionV1 {
  schemaVersion: 'private-local-report-execution-v1';
  acceptanceDigest: string;
  ownerUid: string;
  siteId: string;
}

export interface ReportGenerationJob extends ReportGenerationJobRequest {
  /** Never accepted in public request JSON; atomically excludes automatic dispatch. */
  privateLocalExecution?: PrivateLocalReportExecutionV1;
  /** Seeded at private enqueue, then enriched only after exact source verification. */
  refdataAcceptance?: { digest: string; versions?: Record<string, string>; manifestHashes?: Record<string, string> };
  id: string;
  uid: string;
  /** Authenticated caller that created the job (server stamped). */
  requesterUid?: string;
  /** Owner namespace of the source site/evidence (server resolved). */
  siteOwnerUid?: string;
  /** Owner namespace of the generated report. V2 reports remain requester-owned. */
  reportOwnerUid?: string;
  status: ReportGenerationJobStatus;
  progress: number;
  progressStage: string;
  /** Server-stamped, explicitly approximate runtime baseline for countdown UI. */
  estimatedDurationMs?: number;
  /** Versioned provenance for the estimate; never derived from synthetic progress. */
  estimatedDurationBasis?: 'report-type-baseline-2026-09-v1';
  createdAt: string;
  updatedAt: string;
  /** Stable hash of the normalized request, excluding timestamps/job id. */
  requestHash?: string;
  /** Rerun lineage identity; independent new/add-another jobs get a new id. */
  lineageId?: string;
  /** Monotonic generation number allocated by the lineage coordinator. */
  lineageOrdinal?: number;
  /** Hash-addressed coordinator document that owns the right to finalize this lineage. */
  lineageCoordinatorId?: string;
  /** Frozen enqueue-time persistence pipeline; clients cannot supply this field. */
  reportPipelineVersion?: 'legacy-v1' | 'immutable-v2';
  /** Server-owned route frozen with the enqueue receipt; never a client selector. */
  generationRoute?: import('./reportGenerationRoute').ReportGenerationRouteV1;
  /** Monotonic runner attempt, incremented by the transactional claim. */
  attempt?: number;
  /** Monotonic write sequence for progress/heartbeat updates. */
  progressSeq?: number;
  /** Renewable attempt lease; an expired running attempt may be reclaimed. */
  leaseExpiresAt?: string;
  /** User cancellation intent, distinct from the terminal timestamp. */
  cancelRequestedAt?: string;
  /** Newer job that terminally superseded this attempt, when applicable. */
  supersededByJobId?: string;
  /** Machine-readable terminal classification. */
  terminalReason?: string;
  /** Bounded repair-admission diagnostics recorded when a repair pass is admitted or skipped. */
  repairDiagnostics?: Array<import('../lib/reportGeneration/repairDeadline').ReportRepairDiagnostic & { candidatePath?: string }>;
  /** Explicit repair lifecycle state when a repair pass was skipped under deadline reserve with open hold findings. */
  repairState?: 'resumable' | 'completed' | 'failed';
  /** GCS path to the saved pre-repair candidate when `repairState === 'resumable'`. */
  resumableCandidatePath?: string;
  /** Stable machine-readable error classification. */
  errorCode?: string;
  /** Additive artifact lifecycle seam for the V2 presentation pipeline. */
  artifactState?: ReportArtifactState;
  pdfDelivery?: import('./reportPdfDelivery').ReportPdfDelivery;
  /** Semantic analysis lifecycle is independent from optional artifact work. */
  analysisState?: import('./decisionAnalysis').AnalysisReadinessState;
  analysisRevisionId?: string;
  analysisInputHash?: string;
  analysisContentHash?: string;
  analysisReadiness?: import('./decisionAnalysis').AnalysisReadinessDecisionV1;
  /** Exact persisted content attempt; late source drift may keep it readable
   * while withholding it from the site-owned current ready pointer. */
  reportContentRevision?: import('./reportContent').ReportContentRevisionReferenceV1;
  /** Immutable semantic/presentation revision identity when available. */
  reportRevisionId?: string;
  /** Server-authoritative view/export/publish decision. */
  deliveryDecision?: DeliveryDecision;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  reportId?: string;
  /**
   * Report P4 (job-heartbeat-reaper) — ISO timestamp of the last liveness
   * write. Stamped INSIDE `patchJob`, so every status change AND every
   * throttled progress tick refreshes it for free; a coarse 30s ticker keeps
   * it fresh through silent stretches. The stale-running reaper keys off this
   * field (NOT `updatedAt`, which other writers also bump) to decide whether a
   * `running` job is genuinely dead. Additive — `stripUndefined` keeps it out
   * of legacy / in-flight-at-deploy writes (the reaper falls back to
   * startedAt/updatedAt/createdAt for those).
   */
  heartbeatAt?: string;
  /**
   * Report P4 (job-heartbeat-reaper) — additive discriminator on a FAILED job
   * that was force-failed by the stale-running reaper (heartbeat went stale AND
   * no live Cloud Run lease) rather than by a normal generation error. Lets the
   * jobs surface distinguish a reaped timeout from an in-pipeline failure
   * without parsing the error string. Absent on every other failure.
   */
  failureKind?: 'stale-heartbeat';
  /** Mirrors clientRunId for UI rehydration after reload. */
  clientRunId?: string;
  /** Cloud Run execution name when triggered (audit only). */
  executionName?: string;
  /**
   * R-3 (citation-density fix 5) — `true` on a FAILED job when the failure was
   * the citation-density truth gate (`CitationDensityGateError`). Written by
   * the runner (`executeReportGenerationJob` — previously additive-only via a
   * local `JobPatch` cast; now first-class on the job type) so the jobs
   * surface can branch to the CitationDensityOverrideBanner without parsing
   * the error string. Absent on every other job (`stripUndefined` keeps
   * undefined out of the write).
   */
  blockedByCitationDensity?: boolean;
  /**
   * R-71 — structured provider coverage block when dispatch was blocked.
   */
  blockedByProviderCoverage?: boolean;
  /**
   * R-3 — the FULL citation-density snapshot (density, threshold, uncited
   * claims) persisted alongside `blockedByCitationDensity` so the override
   * banner + uncited-claims list render straight from the job doc. Type-only
   * import — erased at compile time, no runtime coupling to the gate module.
   */
  citationDensity?: import('../lib/audit/citationDensityGate').CitationDensitySnapshot;
  /**
   * Report P4 (quality-hold propagation) — present on a SUCCEEDED job whose
   * generated artifact was quality-held (`resolveQualityHold` fired: a
   * 'needs-revision' review verdict, or a block-severity distribution /
   * section-drift finding). Written by `executeReportGenerationJob`, read by
   * the jobs UI so a held generation never renders as a clean success.
   * `stripUndefined` (via `patchJob`) keeps it out of the write when absent.
   */
  qualityHold?: 'needs-revision';
  /**
   * Report P4 — human-readable hold reasons (already capped at 20 via
   * `QUALITY_HOLD_MAX_FINDINGS` upstream — do NOT re-expand). Mirrors the
   * report metadata doc so the jobs surface can list them without loading
   * the full GCS body.
   */
  qualityHoldFindings?: string[];
  /**
   * Report P4 — the publish-bar mirror for the jobs surface. `true` ⇒ the
   * report is barred from publish/export (quality-held OR draft tier).
   * Readers MUST NOT infer the COST TIER from this — read `generationTier`
   * (via `siteConfigOverrides`) for the tier; `isDraft` is overloaded as the
   * publish bar by design.
   */
  isDraft?: boolean;
}

export const REPORT_GENERATION_JOBS_COLLECTION = 'reportGenerationJobs';

/**
 * Report P4 (job-heartbeat-reaper) — staleness ceiling (15 min). A `running`
 * job whose `heartbeatAt` is older than this AND which has no live Cloud Run
 * lease is force-failed by the stale-running reaper. Co-located here so the
 * runner, the reaper, the cron entrypoint, and the tests read ONE definition
 * (mirrors how `SED_BOOTSTRAP_STALE_MS` is exported once in `sedStaleness.ts`).
 * With the runner's 30s heartbeat ticker, 15 min of silence is 30 missed beats
 * — unambiguously a dead/hung container — and is well under the manifest's 2h
 * (`timeoutSeconds`) Cloud Run wall, so the reaper never races a live task.
 * Deliberately NOT folded into `isActiveReportGenerationJobStatus`.
 */
export const REPORT_GENERATION_STALE_HEARTBEAT_MS = 15 * 60_000;

/**
 * Renewable runner/idempotency lease. Five minutes is long enough to tolerate
 * transient Firestore failures, but short enough to recover a crashed task
 * without the historical 24-hour poison window.
 */
export const REPORT_GENERATION_ATTEMPT_LEASE_MS = 5 * 60_000;

export function reportGenerationJobPath(uid: string, jobId: string): string {
  return `users/${uid}/${REPORT_GENERATION_JOBS_COLLECTION}/${jobId}`;
}

export function isActiveReportGenerationJobStatus(
  status: ReportGenerationJobStatus,
): boolean {
  return status === 'queued' || status === 'running';
}
